import * as React from "react";
import {createRef, useImperativeHandle, useState} from "react";
import {createRoot, type Root} from "react-dom/client";
import {
	CreateWorkingTimeNoticeInitialValues,
	CreateWorkingTimeNoticeDialog,
	DuplicateWorkingTimeNoticeDialog,
	DuplicateWorkingTimeNoticeSource,
	EditWorkingTimeNoticeDialog
} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialog";
import type {WorkingTimeNotice, WorkingTimeType} from "@/shared/timr";

export interface WorkingTimeNoticeDialogConfig {
	workingTimeTypes: WorkingTimeType[];
	showUserRow: boolean;
	isGroupLeaderForThemself: boolean;
}

interface WorkingTimeNoticeDialogRef {
	openForCreate: (initialValues?: CreateWorkingTimeNoticeInitialValues) => void;
	openForEdit: (notice: WorkingTimeNotice) => void;
	openForDuplicate: (source: DuplicateWorkingTimeNoticeSource) => void;
}

interface WorkingTimeNoticeDialogBridgeHostProps {
	ref?: React.Ref<WorkingTimeNoticeDialogRef>;
	workingTimeTypes: WorkingTimeType[];
	onSaved: () => void;
	onDeleted: () => void;
	showUserRow: boolean;
	isGroupLeaderForThemself: boolean;
}

function WorkingTimeNoticeDialogBridgeHost({ref, workingTimeTypes, showUserRow, isGroupLeaderForThemself, onSaved, onDeleted}: WorkingTimeNoticeDialogBridgeHostProps) {
	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<"create" | "edit" | "duplicate">("create");
	const [notice, setNotice] = useState<WorkingTimeNotice | null>(null);
	const [createInitialValues, setCreateInitialValues] = useState<CreateWorkingTimeNoticeInitialValues | null>(null);
	const [duplicateSource, setDuplicateSource] = useState<DuplicateWorkingTimeNoticeSource | null>(null);

	useImperativeHandle(ref, () => ({
		openForCreate: (createInitialValues?: CreateWorkingTimeNoticeInitialValues) => {
			setMode("create");
			setNotice(null);
			setCreateInitialValues(createInitialValues ?? null);
			setOpen(true);
		},
		openForEdit: (nextNotice: WorkingTimeNotice) => {
			setMode("edit");
			setNotice(nextNotice);
			setOpen(true);
		},
		openForDuplicate: (nextSource: DuplicateWorkingTimeNoticeSource) => {
			setMode("duplicate");
			setDuplicateSource(nextSource);
			setOpen(true);
		}
	}), []);

	if (mode === "edit" && notice != null) {
		return (
			<EditWorkingTimeNoticeDialog
				open={open}
				notice={notice}
				workingTimeTypes={workingTimeTypes}
				showUserRow={showUserRow}
				isGroupLeaderForThemself={isGroupLeaderForThemself}
				onClose={() => setOpen(false)}
				onSaved={onSaved}
				onDeleted={onDeleted}
			/>
		);
	}

	if (mode === "duplicate" && duplicateSource != null) {
		return (
			<DuplicateWorkingTimeNoticeDialog
				open={open}
				source={duplicateSource}
				workingTimeTypes={workingTimeTypes}
				showUserRow={showUserRow}
				isGroupLeaderForThemself={isGroupLeaderForThemself}
				onClose={() => setOpen(false)}
				onSaved={onSaved}
				onDeleted={onDeleted}
			/>
		);
	}

	return (
		<CreateWorkingTimeNoticeDialog
			open={open}
			initialValues={createInitialValues}
			workingTimeTypes={workingTimeTypes}
			showUserRow={showUserRow}
			isGroupLeaderForThemself={isGroupLeaderForThemself}
			onClose={() => setOpen(false)}
			onSaved={onSaved}
			onDeleted={onDeleted}
		/>
	);
}

export class WorkingTimeNoticeDialogBridge {

	static readonly EVENT_NOTICE_SAVED = "workingTimeNoticeSaved";
	static readonly EVENT_NOTICE_DELETED = "workingTimeNoticeDeleted";

	private readonly dialogRef = createRef<WorkingTimeNoticeDialogRef>();
	private readonly container: HTMLDivElement;
	private readonly root: Root;
	private destroyed = false;

	constructor(private readonly config: WorkingTimeNoticeDialogConfig) {
		this.container = document.createElement("div");
		document.body.appendChild(this.container);
		this.root = createRoot(this.container, {
			/**
			 * Set a prefix for all generated ids to avoid conflicts with other react roots.
			 */
			identifierPrefix: "working-time-notice-dialog-"
		});
		this.root.render(
			<WorkingTimeNoticeDialogBridgeHost
				ref={this.dialogRef}
				workingTimeTypes={this.config.workingTimeTypes}
				showUserRow={this.config.showUserRow}
				isGroupLeaderForThemself={this.config.isGroupLeaderForThemself}
				onSaved={() => {
					$(this).triggerHandler(WorkingTimeNoticeDialogBridge.EVENT_NOTICE_SAVED);
				}}
				onDeleted={() => {
					$(this).triggerHandler(WorkingTimeNoticeDialogBridge.EVENT_NOTICE_DELETED);
				}}
			/>
		);
	}

	/**
	 * Whether the given working time type is selectable in this dialog, e.g. to check whether a notice can be duplicated.
	 */
	public hasWorkingTimeType(workingTimeTypeId: number): boolean {
		return this.config.workingTimeTypes.some(wtt => wtt.id === workingTimeTypeId);
	}

	public openForCreate(initialValues?: CreateWorkingTimeNoticeInitialValues) {
		if (this.destroyed) return;
		this.dialogRef.current?.openForCreate(initialValues);
	}

	public openForEdit(notice: WorkingTimeNotice) {
		if (this.destroyed) return;
		this.dialogRef.current?.openForEdit(notice);
	}

	public openForDuplicate(source: DuplicateWorkingTimeNoticeSource) {
		if (this.destroyed) return;
		this.dialogRef.current?.openForDuplicate(source);
	}

	public destroy() {
		if (this.destroyed) return;
		$(this).off();
		this.root.unmount();
		this.container.remove();
		this.destroyed = true;
	}
}
